import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { buildAdminIntakeHubPath } from '../adminJourneyRouting';
import type { IntakeFleetResponse } from '../types';

const SERVICE_LABELS: Record<string, string> = {
  jotbill: 'JotBill',
  jotcred: 'JotCred',
  jotsite: 'JotSite',
};

export type FleetTrack = 'overview' | 'credentialing' | 'ehr-import';

export function StaffIntakeFleet({
  data,
  loading,
  failed,
  search,
  page,
  perPage,
  track,
  returnTo,
  canVerify,
  onSearch,
  onPage,
  onRefresh,
}: {
  data?: IntakeFleetResponse;
  loading: boolean;
  failed: boolean;
  search: string;
  page: number;
  perPage: number;
  track: FleetTrack;
  returnTo: string;
  canVerify: boolean;
  onSearch: (value: string) => void;
  onPage: (value: number) => void;
  onRefresh: () => void;
}) {
  const path = (organizationId: string, target: FleetTrack | 'billing' | 'verification') =>
    buildAdminIntakeHubPath({
      organizationId,
      stage:
        target === 'overview'
          ? 'agreements'
          : target === 'verification'
            ? 'verification'
            : 'go-live',
      statusTab: target === 'overview' || target === 'verification' ? undefined : target,
      returnTo,
    });
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Search organizations"
          size="small"
          value={search}
          onChange={event => onSearch(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 100 } }}
          sx={{ flex: 1 }}
        />
        <Button variant="outlined" disabled={loading} onClick={onRefresh}>
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" aria-live="polite">
        {data
          ? `${data.total == null ? 'Unknown total of' : `About ${data.total}`} organizations matching search · includes organizations without intake configuration`
          : 'All organizations, including those without intake configuration'}
      </Typography>
      {track === 'credentialing' && (
        <Alert severity="info">
          Counts describe individual provider/payer entries, not overall practice completion. Open
          the Intake Hub to review or update entries.
        </Alert>
      )}
      {track === 'ehr-import' && <Alert severity="info">EHR migration is coming soon.</Alert>}
      {failed && (
        <Alert
          severity={data ? 'warning' : 'error'}
          action={
            <Button color="inherit" onClick={onRefresh}>
              Retry
            </Button>
          }
        >
          {data
            ? 'Refresh failed. Previously loaded evidence remains visible and may be stale.'
            : 'Could not load organizations. Retry to check their stored evidence.'}
        </Alert>
      )}
      {loading && <LinearProgress aria-label="Loading organization evidence" />}
      {data && (
        <TableContainer
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}
        >
          <Table aria-label="Staff intake organizations">
            <TableHead>
              <TableRow>
                <TableCell>Organization</TableCell>
                {track === 'overview' && (
                  <>
                    <TableCell>Services</TableCell>
                    <TableCell>Verification</TableCell>
                    <TableCell>Billing</TableCell>
                  </>
                )}
                {track !== 'ehr-import' && (
                  <TableCell>Credentialing · provider/payer entries</TableCell>
                )}
                {track !== 'credentialing' && <TableCell>EHR Migration</TableCell>}
                <TableCell>Intake Hub</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(row => (
                <TableRow key={row.organization_id}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>{row.organization_name}</Typography>
                  </TableCell>
                  {track === 'overview' && (
                    <>
                      <TableCell>
                        {row.selected_services == null
                          ? 'Selection unknown'
                          : row.selected_services.length
                            ? row.selected_services
                                .map(service => SERVICE_LABELS[service] ?? service)
                                .join(', ')
                            : 'No services selected'}
                      </TableCell>
                      <TableCell>
                        {!canVerify ? (
                          'Admin access required'
                        ) : row.verification ? (
                          <Stack spacing={0.5}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={
                                row.verification.state === 'awaiting'
                                  ? 'Awaiting verification'
                                  : row.verification.state === 'verified'
                                    ? 'Verified'
                                    : 'Incomplete'
                              }
                            />
                            <Typography variant="caption">
                              {row.verification.provider_counts.verified} of{' '}
                              {row.verification.provider_counts.total} roster providers verified
                            </Typography>
                            <Button
                              size="small"
                              component={Link}
                              to={path(row.organization_id, 'verification')}
                            >
                              Review
                            </Button>
                          </Stack>
                        ) : (
                          'No eligible verification evidence'
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            {row.billing_connection_recorded == null
                              ? 'Connection evidence unknown'
                              : row.billing_connection_recorded
                                ? 'Connection date recorded'
                                : 'No connection date recorded'}
                          </Typography>
                          <Button
                            size="small"
                            component={Link}
                            to={path(row.organization_id, 'billing')}
                          >
                            Review billing
                          </Button>
                        </Stack>
                      </TableCell>
                    </>
                  )}
                  {track !== 'ehr-import' && (
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          {row.credentialing ? (
                            <>
                              {row.credentialing.total ?? 'Unknown'} recorded ·{' '}
                              {row.credentialing.complete ?? 'Unknown'} complete
                            </>
                          ) : (
                            'Credentialing evidence unavailable'
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.credentialing?.in_progress ?? '—'} in progress ·{' '}
                          {row.credentialing?.not_started ?? '—'} not started
                        </Typography>
                        {row.credentialing_updated_at && (
                          <Typography variant="caption" color="text.secondary">
                            Last record update{' '}
                            {new Date(row.credentialing_updated_at).toLocaleDateString()}
                          </Typography>
                        )}
                        {track === 'overview' && (
                          <Button
                            size="small"
                            component={Link}
                            to={path(row.organization_id, 'credentialing')}
                          >
                            Review records
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                  {track !== 'credentialing' && (
                    <TableCell>
                      <Chip label="Coming soon" size="small" variant="outlined" />
                    </TableCell>
                  )}
                  <TableCell>
                    <Button
                      component={Link}
                      to={path(row.organization_id, track)}
                      variant="outlined"
                      size="small"
                    >
                      Open Intake Hub
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data.items.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography>
                {data.total
                  ? 'No organizations on this page.'
                  : 'No organizations match this search.'}
              </Typography>
              {(data.total ?? 0) > 0 && <Button onClick={() => onPage(1)}>First page</Button>}
            </Box>
          )}
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={-1}
        page={page - 1}
        rowsPerPage={perPage}
        rowsPerPageOptions={[perPage]}
        onPageChange={(_event, next) => onPage(next + 1)}
        labelRowsPerPage="Organizations per page"
        labelDisplayedRows={() => {
          const size = data?.items.length ?? 0;
          const from = size ? (page - 1) * perPage + 1 : 0;
          const to = size ? (page - 1) * perPage + size : 0;
          return `${from}–${to} of about ${data?.total ?? 0}`;
        }}
        slotProps={{
          actions: {
            nextButton: { disabled: loading || failed || (data?.items.length ?? 0) < perPage },
          },
        }}
      />
    </Stack>
  );
}
